import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSlidingPill } from "@/components/shared/Pill";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { stockQueueQuery, dupPairsQuery, dupCountQuery, refreshEcoBadges, freshest } from "@/lib/ecoQueries";
import { HeaderSlot } from "@/components/shared/ListToolbar";
import { PieChart, FileText, Wallet, Package, Layers, Banknote } from "lucide-react";
import EcoOverview from "@/components/economics/overview/EcoOverview";
import { Button } from "@/components/ui/button";
import { Download, ClipboardList, ChevronDown, Table2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useExportDownload } from "@/hooks/useExportDownload";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MenuItems } from "@/components/shared/ListToolbar";
import { sheetMenuItems, downloadSheet, rangeOfMonths, rangeLabel, PAY_ROLES, SHEET_ROLES } from "@/lib/sheetExport";
import { downloadSigned } from "@/lib/api";
import EconomicsDocuments from "@/pages/EconomicsDocuments";
import EconomicsPayments from "@/pages/EconomicsPayments";
import EconomicsCashbox from "@/pages/EconomicsCashbox";
import EconomicsStock from "@/pages/EconomicsStock";
import ExpensesOverview from "@/pages/ExpensesOverview";

const TABS = [
  { to: "/ekonomika", label: "Přehled", icon: PieChart, testid: "eco-tab-overview", end: true },
  { to: "/ekonomika/doklady", label: "Doklady", icon: FileText, testid: "eco-tab-documents", badge: "dups" },
  { to: "/ekonomika/platby", label: "Platby", icon: Wallet, testid: "eco-tab-payments" },
  { to: "/ekonomika/pokladna", label: "Pokladna", icon: Banknote, testid: "eco-tab-cashbox" },
  { to: "/ekonomika/naskladneni", label: "Naskladnění", icon: Package, testid: "eco-tab-stock", badge: "stock" },
  { to: "/ekonomika/rezie", label: "Režie", icon: Layers, testid: "eco-tab-overhead" },
];

export default function Economics({ tab }) {
  // Číslovka u Naskladnění drží povědomí, že něco visí, i když je fronta na jiné
  // záložce. Přepočítá se po každé změně dokladu (událost `pp-doc-saved`).
  // 220: stejné react-query klíče jako záložky Doklady a Naskladnění — obal a záložka
  // sdílí jeden požadavek. Na Dokladech se počet duplicit bere z plného seznamu dvojic
  // (ten tam stejně je), jinde z lehkého /duplicates/count.
  const qc = useQueryClient();
  const onDocs = tab === "documents";
  const stock = useQuery(stockQueueQuery);
  const dupFull = useQuery({ ...dupPairsQuery, enabled: onDocs });
  const dupLight = useQuery({ ...dupCountQuery, enabled: !onDocs });
  const stockCount = stock.data?.count || 0;
  // Číslovka u Dokladů = otevřené dvojice duplicit (153) — stejný vzor jako fronta naskladnění.
  const dupCount = freshest(dupFull, dupLight)?.open_count || 0;
  useEffect(() => {
    const load = () => { refreshEcoBadges(qc); qc.invalidateQueries({ queryKey: ["eco-overview"] }); };   // 227: i Přehled
    window.addEventListener("pp-doc-saved", load);
    return () => window.removeEventListener("pp-doc-saved", load);
  }, [qc]);

  // 403: podklad pro mzdy — účetní má jen Ekonomiku, šichtovnici a částky si stáhne odsud za měsíc z Od–Do
  const role = useAuth()?.user?.role;
  const canSheet = SHEET_ROLES.includes(role);
  const { busy: exporting, run: runExport } = useExportDownload();
  const payMenu = (p) => {
    const range = rangeOfMonths(p.od, p.do), label = rangeLabel(range);
    const items = sheetMenuItems({ label, canMoney: PAY_ROLES.includes(role), icons: { pdf: FileText, xlsx: Table2, csv: Download, money: Banknote },
      onPick: (fmt, money) => downloadSheet({ range, label, fmt, money, run: runExport,
        fillHint: "Doplní je vedoucí nebo asistentka v Docházce › Šichtovnice." }) });
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-10 rounded-full shrink-0 px-4 font-bold gap-2" disabled={!!exporting}
            title={`Šichtovnice a podklad pro mzdy · ${label}`} data-testid="eco-payroll-btn">
            <ClipboardList size={17} /> <span className="hidden sm:inline">Podklad pro mzdy</span><span className="sm:hidden">Mzdy</span> <ChevronDown size={15} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[250px] max-h-[70vh] overflow-y-auto" data-testid="eco-payroll-menu"><MenuItems items={items} /></DropdownMenuContent>
      </DropdownMenu>
    );
  };
  // 352: místo vpravo v hlavičce pro akce záložky (Doklady: Hledat · ⋯ · Nahrát doklad)
  const [slot, setSlot] = useState(null);
  // 385: podtržení vybrané záložky dojede s pružinou (záložky = adresy, aktivní NavLink má aria-current)
  const path = useLocation().pathname;
  const ul = useSlidingPill(path, `${stockCount}|${dupCount}`, { selector: '[aria-current="page"]' });
  return (
    <div className="space-y-5" data-testid="economics-module">
      <div className="flex flex-wrap items-start gap-x-2 gap-y-3" data-testid="page-header" data-pp-static>
        <h1 className="text-3xl font-black tracking-tighter uppercase min-w-[160px] flex-1">Ekonomika</h1>
        <div ref={setSlot} className="contents" data-testid="eco-header-actions" />
      </div>

      <div className="border-b border-border -mt-1" data-pp-static>
        <nav ref={ul.ref} className="relative flex gap-1 overflow-x-auto no-scrollbar" data-testid="eco-tabs">
          {ul.pill("bg-brand", { underline: true })}
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 min-h-[44px] text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  isActive ? `${ul.ready ? "border-transparent" : "border-brand"} text-brand` : "border-transparent text-muted-foreground hover:text-foreground"
                }`}>
              <t.icon size={16} /> {t.label}
              {t.badge === "stock" && stockCount > 0 && (
                <span data-testid="eco-tab-stock-badge"
                  className="font-heading font-extrabold text-[11px] bg-amber-500 text-white rounded-full px-2 py-0.5 tabular-nums">
                  {stockCount}
                </span>
              )}
              {t.badge === "dups" && dupCount > 0 && (
                <span data-testid="eco-tab-docs-badge"
                  className="font-heading font-extrabold text-[11px] bg-amber-500 text-white rounded-full px-2 py-0.5 tabular-nums">
                  {dupCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      {tab === "overview" && (
        <EcoOverview actions={(p) => <>
          {canSheet && payMenu(p)}
          <Button onClick={() => downloadSigned("/api/economics/company/export", "ekonomika_firmy.csv")} variant="outline" size="icon"
            className="h-10 w-10 rounded-full shrink-0" aria-label="Export zakázek do CSV" title="Export zakázek do CSV" data-testid="eco-export-btn">
            <Download size={17} />
          </Button></>} />
      )}
      <HeaderSlot.Provider value={slot}>
        {tab === "documents" && <EconomicsDocuments />}
        {tab === "payments" && <EconomicsPayments />}
        {tab === "cashbox" && <EconomicsCashbox />}
        {tab === "stock" && <EconomicsStock />}
        {tab === "overhead" && <ExpensesOverview embedded />}
      </HeaderSlot.Provider>
    </div>
  );
}
